import { useEffect, useState } from "react";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const ranges = [
  { value: "24h", label: "24h" },
  { value: "7d", label: "7d" },
  { value: "30d", label: "30d" },
  { value: "90d", label: "90d" },
  { value: "1y", label: "1y" },
];

const nf = new Intl.NumberFormat();
const pct = (value) => `${Number(value || 0).toFixed(1)}%`;

function Card({ label, value, hint }) {
  return <div className="traffic-stat"><span>{label}</span><b>{value}</b>{hint && <small>{hint}</small>}</div>;
}

function Table({ title, rows, columns, empty = "No data yet." }) {
  return (
    <div className="traffic-table-card">
      <h3>{title}</h3>
      {!rows || rows.length === 0 ? <p className="traffic-empty">{empty}</p> :
        <div className="traffic-table-scroll"><table><thead><tr>{columns.map((column) => <th key={column.key}>{column.label}</th>)}</tr></thead>
          <tbody>{rows.map((row, index) => <tr key={index}>{columns.map((column) => <td key={column.key}>{column.render ? column.render(row) : row[column.key]}</td>)}</tr>)}</tbody>
        </table></div>}
    </div>
  );
}

export default function TrafficInsightsWidget() {
  const [range, setRange] = useState("30d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = () => {
    setLoading(true);
    setError("");
    fetch(`/api/traffic_analytics.php?range=${encodeURIComponent(range)}`, { credentials: "same-origin", headers: { Accept: "application/json" } })
      .then(async (response) => {
        const text = await response.text();
        let json;
        try { json = JSON.parse(text); } catch (_) { throw new Error(`Invalid analytics response: ${text.slice(0, 180)}`); }
        if (!response.ok || json.error) throw new Error(json.error || `HTTP ${response.status}`);
        return json;
      })
      .then(setData)
      .catch((err) => setError(err.message || "Unable to load traffic analytics."))
      .finally(() => setLoading(false));
  };

  useEffect(load, [range]);

  const summary = data?.summary || {};
  const series = data?.time_series || [];
  const devices = data?.device_breakdown || [];

  return (
    <section className="admin-panel traffic-insights">
      <div className="traffic-heading">
        <div>
          <h2>Traffic & Funnel Performance</h2>
          <p>Public visitors, acquisition sources, funnel progress, and submitted requests.</p>
          {data?.data_source && <small>Scope: Family Income Guard only · Data source: {data.data_source}</small>}
        </div>
        <div className="traffic-controls">
          {ranges.map((item) => <button key={item.value} type="button" onClick={() => setRange(item.value)} className={range === item.value ? "active" : ""}>{item.label}</button>)}
          <button type="button" onClick={load} className="refresh">Refresh</button>
        </div>
      </div>

      {error && <div className="admin-alert error">{error}</div>}
      {loading && <div className="traffic-loading">Loading traffic analytics…</div>}

      <div className="traffic-stat-grid">
        <Card label="Visitors" value={nf.format(summary.visitors || 0)} hint="Unique public visitors" />
        <Card label="Sessions" value={nf.format(summary.sessions || 0)} hint="30-minute public sessions" />
        <Card label="Page Views" value={nf.format(summary.pageviews || 0)} hint={`${nf.format(summary.bot_or_internal_events || 0)} admin, bot, or non-public events excluded`} />
        <Card label="Funnel Starts" value={nf.format(summary.funnel_starts || 0)} hint="Unique sessions that opened the application" />
        <Card label="Submitted Requests" value={nf.format(summary.lead_submissions || 0)} hint="Successfully saved lead submissions" />
        <Card label="Funnel Conversion" value={pct(summary.funnel_conversion_rate)} hint="Submitted requests ÷ funnel starts" />
        <Card label="Active Now" value={nf.format(summary.active_last_15m || 0)} hint="Public visitors in the last 15 minutes" />
      </div>

      <div className="traffic-chart-grid">
        <div className="traffic-chart-card traffic-trend">
          <h3>Traffic and submitted requests</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={series}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Line type="monotone" dataKey="pageviews" stroke="#0f172a" strokeWidth={2} dot={false} name="Page views" />
              <Line type="monotone" dataKey="visitors" stroke="#0891b2" strokeWidth={2} dot={false} name="Visitors" />
              <Line type="monotone" dataKey="leads" stroke="#b7791f" strokeWidth={2} dot={false} name="Submitted requests" />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="traffic-chart-card">
          <h3>Sessions by device</h3>
          {devices.length === 0 ? <p className="traffic-empty">No public device data in this range yet.</p> :
            <ResponsiveContainer width="100%" height={300}><PieChart><Pie data={devices} dataKey="count" nameKey="label" outerRadius={100} label>
              {devices.map((_, index) => <Cell key={index} fill={["#0f172a", "#0891b2", "#14b8a6", "#64748b", "#94a3b8"][index % 5]} />)}
            </Pie><Tooltip /></PieChart></ResponsiveContainer>}
        </div>
      </div>

      <div className="traffic-table-grid">
        <div className="traffic-wide"><Table title="Funnel Progress" rows={data?.funnel_progress || []} empty="No funnel activity in this range yet." columns={[
          { key: "step", label: "Milestone" },
          { key: "sessions", label: "Unique Sessions", render: (row) => nf.format(row.sessions || 0) },
          { key: "completion_rate", label: "% of Starts", render: (row) => pct(row.completion_rate) },
        ]} /></div>
        <Table title="Top Pages" rows={data?.top_pages || []} columns={[
          { key: "path", label: "Page", render: (row) => <a href={row.path} target="_blank" rel="noreferrer">{row.path}</a> },
          { key: "views", label: "Views", render: (row) => nf.format(row.views || 0) },
          { key: "visitors", label: "Visitors", render: (row) => nf.format(row.visitors || 0) },
        ]} />
        <Table title="Landing Pages" rows={data?.landing_pages || []} columns={[
          { key: "path", label: "First Page", render: (row) => <a href={row.path} target="_blank" rel="noreferrer">{row.path}</a> },
          { key: "sessions", label: "Sessions", render: (row) => nf.format(row.sessions || 0) },
        ]} />
        <Table title="Referrers / Sources" rows={data?.top_referrers || []} columns={[
          { key: "source", label: "Source" },
          { key: "sessions", label: "Sessions", render: (row) => nf.format(row.sessions || 0) },
          { key: "views", label: "Views", render: (row) => nf.format(row.views || 0) },
        ]} />
        <Table title="UTM Campaigns" rows={data?.utm_campaigns || []} empty="No UTM-tagged traffic yet. Configure Meta URL parameters under Meta setup." columns={[
          { key: "campaign", label: "Campaign" },
          { key: "source_medium", label: "Source / Medium" },
          { key: "sessions", label: "Sessions", render: (row) => nf.format(row.sessions || 0) },
        ]} />
      </div>

      <p className="traffic-note">Admin/login activity, API/static requests, and obvious bots are excluded. Funnel milestones come from Family Income Guard funnel sessions; submitted requests come from the lead intake table. Campaign setup instructions are under Meta setup.</p>
    </section>
  );
}
